/*
 * "Anlagen, Touchpanels": alle TP8 Pro und TP10 Pro über alle Anlagen im
 * eigenen Datenbereich, Android wie sinnux in einer Liste. Nur Verwaltung des
 * Geräts (OTA, Fernzugriff, Einstellungen), keine Anlagenfunktionen; die
 * bleiben auf den Anlagenseiten. Entwurf: docs/entwurf-touchpanel-verwaltung.md.
 */
import { useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import type { ColumnDef } from '@tanstack/react-table';
import { AppShell } from '../components/AppShell';
import { DataTable } from '../components/ui/DataTable';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useAsync } from '../hooks/useAsync';
import { api, ApiError } from '../api/client';
import { formatRelative } from '../settings/deviceRegistrations';
import {
  EMPTY_FILTER,
  filterTouchpanels,
  modelLabel,
  panelLabel,
  platformLabel,
  type ListTouchpanelsResponse,
  type TouchpanelDto,
  type TouchpanelFilter,
} from '../components/touchpanelModel';
import './TouchpanelsPage.css';

/** Ein Wert oder der Hinweis, dass das Panel ihn nicht meldet. */
export function Reported({ value, mono }: { value: string | null | undefined; mono?: boolean }) {
  if (!value) return <span class="muted hv-touchpanels__missing">nicht gemeldet</span>;
  return <span class={mono ? 'mono' : undefined}>{value}</span>;
}

export function OnlineBadge({ p }: { p: Pick<TouchpanelDto, 'online' | 'lastSeenAt'> }) {
  return p.online
    ? <StatusBadge tone="ok">online</StatusBadge>
    : <StatusBadge tone="neutral" title={new Date(p.lastSeenAt).toLocaleString('de-AT')}>
        zuletzt {formatRelative(p.lastSeenAt)}
      </StatusBadge>;
}

export function TouchpanelsPage() {
  const { route } = useLocation();
  const [tick, setTick] = useState(0);
  const [filter, setFilter] = useState<TouchpanelFilter>(EMPTY_FILTER);
  const state = useAsync(
    (signal) => api.get<ListTouchpanelsResponse>('/api/touchpanels', signal),
    [tick],
  );

  const all = state.status === 'ok' ? state.data.touchpanels : [];
  const rows = useMemo(() => filterTouchpanels(all, filter), [all, filter]);

  const columns: ColumnDef<TouchpanelDto, unknown>[] = [
    {
      id: 'name', accessorFn: (p) => panelLabel(p), header: 'Name',
      cell: ({ row }) => <span class="hv-touchpanels__name">{panelLabel(row.original)}</span>,
    },
    {
      id: 'online', accessorFn: (p) => (p.online ? 1 : 0), header: 'Zustand',
      cell: ({ row }) => <OnlineBadge p={row.original} />,
    },
    {
      id: 'model', accessorFn: (p) => modelLabel(p.model), header: 'Typ',
      cell: ({ row }) => (row.original.model
        ? modelLabel(row.original.model)
        : <span class="muted" title={row.original.chassis ?? ''}>unbekannt</span>),
    },
    {
      id: 'platform', accessorFn: (p) => p.platform, header: 'System',
      cell: ({ row }) => (
        <span title={row.original.osPretty ?? ''}>{platformLabel(row.original.platform)}</span>
      ),
    },
    {
      id: 'plant', accessorFn: (p) => p.plantName ?? '', header: 'Anlage',
      cell: ({ row }) => (
        <a href={`/plants/${row.original.plantId}`} onClick={(e) => e.stopPropagation()}>
          {row.original.plantName ?? row.original.plantId.slice(0, 8)}
        </a>
      ),
    },
    {
      id: 'serial', accessorFn: (p) => p.chipSerial ?? p.serial ?? '', header: 'Seriennummer',
      cell: ({ row }) => <Reported mono value={row.original.chipSerial ?? row.original.serial} />,
    },
    {
      id: 'app', accessorFn: (p) => p.appVersion ?? '', header: 'App / Client',
      cell: ({ row }) => <Reported mono value={row.original.appVersion} />,
    },
    {
      id: 'netpilot', accessorFn: (p) => p.netPilotVersion ?? '', header: 'NetPilot',
      cell: ({ row }) => <Reported mono value={row.original.netPilotVersion} />,
    },
    {
      id: 'ip', accessorFn: (p) => p.addresses[0]?.ip ?? '', header: 'IP',
      cell: ({ row }) => (row.original.addresses.length > 0
        ? <span class="mono" title={row.original.addresses.map((a) => `${a.iface} ${a.ip}`).join('\n')}>
            {row.original.addresses[0].ip}
            {row.original.addresses.length > 1 && <span class="muted"> +{row.original.addresses.length - 1}</span>}
          </span>
        : <Reported value={null} />),
    },
  ];

  const toolbar = (
    <div class="hv-touchpanels__filters">
      <input type="search" placeholder="Suchen: Name, Anlage, Seriennummer, IP, Fassung"
             value={filter.text} aria-label="Touchpanels durchsuchen"
             onInput={(e) => setFilter({ ...filter, text: (e.target as HTMLInputElement).value })} />
      <select value={filter.platform} aria-label="System"
              onChange={(e) => setFilter({ ...filter, platform: (e.target as HTMLSelectElement).value as TouchpanelFilter['platform'] })}>
        <option value="">Alle Systeme</option>
        <option value="android">Android</option>
        <option value="sinnux">sinnux</option>
      </select>
      <select value={filter.model} aria-label="Typ"
              onChange={(e) => setFilter({ ...filter, model: (e.target as HTMLSelectElement).value as TouchpanelFilter['model'] })}>
        <option value="">Alle Typen</option>
        <option value="tp8pro">TP8 Pro</option>
        <option value="tp10pro">TP10 Pro</option>
        <option value="unknown">unbekannt</option>
      </select>
      <select value={filter.online} aria-label="Zustand"
              onChange={(e) => setFilter({ ...filter, online: (e.target as HTMLSelectElement).value as TouchpanelFilter['online'] })}>
        <option value="">online und offline</option>
        <option value="online">nur online</option>
        <option value="offline">nur offline</option>
      </select>
      <span class="muted hv-touchpanels__count">{rows.length} von {all.length}</span>
      <button type="button" class="hv-btn hv-btn--sm" onClick={() => setTick((t) => t + 1)}>Neu laden</button>
    </div>
  );

  return (
    <AppShell title="Touchpanels">
      <div class="hv-touchpanels">
        <p class="muted hv-touchpanels__intro">
          TP8 Pro und TP10 Pro mit Android oder sinnux. Hier nur Updates, Fernzugriff und Einstellungen des Panels;
          Datenpunkte und Anlagenfunktionen liegen bei der Anlage.
        </p>
        {state.status === 'loading' && <p class="muted">Lade Touchpanels…</p>}
        {state.status === 'error' && (
          <p class="hv-touchpanels__error">
            {state.error instanceof ApiError && state.error.status === 403
              ? 'Dafür fehlt dir das Recht manage.read.'
              : 'Touchpanels konnten nicht geladen werden.'}
          </p>
        )}
        {state.status === 'ok' && (
          <>
            {state.data.truncated && (
              <p class="hv-touchpanels__error">Mehr Panels als angezeigt; bitte über die Anlage eingrenzen.</p>
            )}
            <DataTable<TouchpanelDto>
              columns={columns}
              rows={rows}
              rowKey={(p) => p.clientId}
              onRowClick={(p) => route(`/touchpanels/${p.clientId}`)}
              initialSorting={[{ id: 'name', desc: false }]}
              emptyLabel={all.length === 0 ? 'Keine Touchpanels im Datenbereich.' : 'Kein Panel passt zum Filter.'}
              toolbar={toolbar}
            />
          </>
        )}
      </div>
    </AppShell>
  );
}
